import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import translations from './../data/translations';
import { generateUniqueRandomName } from './../data/nameGenerator';

export default function NicknamePage() {
  const navigate = useNavigate();
  const [generatedName, setGeneratedName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'nl');

  const t = translations[language] || translations.en;

  useEffect(() => {
    const stored = localStorage.getItem('nickname');
    if (stored) navigate('/home');
  }, [navigate]);

  const apiUrl = window.APP_CONFIG?.apiBaseUrl;

  const checkNameExists = async (name) => {
    const res = await fetch(`${apiUrl}/check-nickname/${name}`, {
      headers: { 'X-Game-Token': 'b73ebff9-4537-477b-97f5-ebb968bfbe40' },
    });
    const data = await res.json();
    return data.exists;
  };

  useEffect(() => {
    const generateName = async () => {
      if (!apiUrl) return;
      setIsGenerating(true);
      try {
        const uniqueName = await generateUniqueRandomName(checkNameExists);
        setGeneratedName(uniqueName);
      } catch (err) {
        console.error('Error generating name:', err);
        setError(t.generateError);
      } finally {
        setIsGenerating(false);
      }
    };
    generateName();
  }, [apiUrl, t.generateError]); // re-run only if error text changes

  const handleAcceptName = () => {
    if (generatedName) {
      localStorage.setItem('nickname', generatedName);
      localStorage.setItem('hasPlayed', 'false');
      navigate('/home');
    }
  };

  const handleGenerateNewName = async () => {
    setIsGenerating(true);
    setError('');
    try {
      const uniqueName = await generateUniqueRandomName(checkNameExists);
      setGeneratedName(uniqueName);
    } catch (err) {
      console.error('Error generating name:', err);
      setError(t.generateError);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleLanguageChange = (e) => {
    const selected = e.target.value;
    localStorage.setItem('language', selected);
    setLanguage(selected);
  };

  return (
    <div className="min-h-screen bg-[#0d1b2a] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Gradient Blobs */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute w-72 h-72 bg-[#00aca9] opacity-20 rounded-full blur-3xl top-10 left-[-100px]" />
        <div className="absolute w-80 h-80 bg-[#f68d43] opacity-20 rounded-full blur-3xl bottom-10 right-[-80px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white shadow-2xl rounded-2xl p-8 w-full max-w-md text-center z-10"
      >
        <div className="flex justify-end mb-2">
          <select
            value={language}
            onChange={handleLanguageChange}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm"
          >
            <option value="nl">NL</option>
            <option value="en">EN</option>
            <option value="fr">FR</option>
          </select>
        </div>

        <img src="/logo.png" alt="Swishing Logo" className="h-16 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-[#2b3b4b] mb-2">{t.welcomeShort}</h1>
        <p className="text-sm text-gray-600 mb-6">{t.nicknameGenerated}</p>

        <div className="flex flex-col gap-4">
          <div className="text-sm font-medium text-left text-[#2b3b4b]">
            {t.generatedNameLabel}
          </div>

          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 min-h-[60px] flex items-center justify-center">
            {isGenerating ? (
              <div className="flex items-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#00aca9]"></div>
                {t.generating}
              </div>
            ) : generatedName ? (
              <span className="text-xl font-bold text-[#2b3b4b]">{generatedName}</span>
            ) : (
              <span className="text-gray-500">{t.loading}</span>
            )}
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <div className="flex gap-3">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleGenerateNewName}
              disabled={isGenerating || !generatedName}
              className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.newName}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleAcceptName}
              disabled={isGenerating || !generatedName}
              className="flex-1 bg-[#00aca9] hover:bg-[#00918d] text-white font-semibold py-2 px-4 rounded-lg shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.accept}
            </motion.button>
          </div>

          <p className="text-xs text-gray-500 mt-2">{t.anonymityNote}</p>
        </div>
      </motion.div>
    </div>
  );
}
